import { FC, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Ban } from "lucide-react";
import { toast } from "sonner";
import { useProfile } from '@/hooks/useProfile';

interface CancelSubscriptionDialogProps {
  userId: string;
  onCanceled: () => void;
}

export const CancelSubscriptionDialog: FC<CancelSubscriptionDialogProps> = ({
  userId,
  onCanceled
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const { user, profile, refreshProfile } = useProfile();

  const handleCancel = async () => {
    if (confirmText !== "CANCELAR") {
      toast.error("Por favor, escribe CANCELAR para confirmar");
      return;
    }

    try {
      setIsLoading(true);

      // Usar la URL base del sitio ya que el worker está configurado para la misma ruta
      const response = await fetch(`${window.location.origin}/api/subscription/cancel`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Origin': window.location.origin
        },
        body: JSON.stringify({
          user_id: userId,
        }),
        credentials: 'omit',
        mode: 'cors'
      });

      if (!response.ok) {
        throw new Error('Error al cancelar la suscripción');
      }

      const data = await response.json();
      const endDate = new Date(data.subscription_end_date);
      
      toast.success('Suscripción cancelada exitosamente', {
        description: `Tu suscripción Premium estará activa hasta el ${endDate.toLocaleDateString('es-ES', {
          day: 'numeric',
          month: 'long',
          year: 'numeric'
        })}`
      });
      
      await refreshProfile();
      setIsOpen(false);
      onCanceled();
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al cancelar la suscripción');
    } finally {
      setIsLoading(false);
    }
  };

  const handleReactivate = async () => {
    try {
      setIsLoading(true);
      const response = await fetch(`${window.location.origin}/api/subscription/reactivate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Origin': window.location.origin
        },
        body: JSON.stringify({
          user_id: userId,
        }),
        credentials: 'omit',
        mode: 'cors'
      });

      if (!response.ok) {
        throw new Error('Error al reactivar la suscripción');
      }

      toast.success('Suscripción reactivada exitosamente');
      await refreshProfile();
      setIsOpen(false);
      onCanceled();
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al reactivar la suscripción');
    } finally {
      setIsLoading(false);
    }
  };

  // Si la suscripción está cancelada pero aún activa, mostrar la opción de reactivar
  if (profile?.subscription_status === 'canceled' && profile?.subscription_end_date && new Date(profile.subscription_end_date) > new Date()) {
    return (
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reactivar Suscripción Premium</DialogTitle>
            <DialogDescription>
              Tu suscripción está cancelada pero aún tienes acceso Premium hasta el{' '}
              {new Date(profile.subscription_end_date).toLocaleDateString('es-ES', {
                day: 'numeric',
                month: 'long',
                year: 'numeric'
              })}
              . ¿Deseas reactivar tu suscripción?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsOpen(false)} disabled={isLoading}>
              Cancelar
            </Button>
            <Button onClick={handleReactivate} disabled={isLoading}>
              {isLoading ? 'Reactivando...' : 'Reactivar Suscripción'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          className="w-full sm:w-auto bg-red-50 hover:bg-red-100 border-red-200 text-red-600 hover:text-red-700"
          onClick={() => setIsOpen(true)}
        >
          <Ban className="mr-2 h-4 w-4" />
          Cancelar Suscripción
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Cancelar Suscripción Premium</DialogTitle>
          <DialogDescription>
            ¿Estás seguro que deseas cancelar tu suscripción Premium? Para confirmar, escribe "CANCELAR" en el campo de abajo.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="confirm">
              Escribe <span className="font-bold">CANCELAR</span> para confirmar
            </Label>
            <Input
              id="confirm"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="Escribe CANCELAR para confirmar"
              className="col-span-3"
            />
          </div>
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => setIsOpen(false)}
            disabled={isLoading}
          >
            Volver
          </Button>
          <Button
            variant="destructive"
            onClick={handleCancel}
            disabled={isLoading || confirmText !== "CANCELAR"}
          >
            {isLoading ? 'Cancelando...' : 'Confirmar Cancelación'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
